/**
 * Read-only raw-events table of one track (W2-05; DESIGN.md §4.18 Raw, U§5 C7): every event the export writes, with
 * its start and end beats (the exact triples), start and end values, easing number, curve part, Bezier handles, link
 * group and what it was written for (segment, single key, jump at the end, lead-in, RPE padding, baked speed piece).
 * Long lists are virtualised; activating a row goes to the segment the event comes from. "Copy as RPE JSON" copies
 * exactly the exported list.
 */

import type { IrEvent } from '../../core/ir/types';
import type { RGB } from '../../core/util/types';
import { Button, VirtualList } from '../../ui';
import { useText } from './hooks';
import { m } from './raw.messages';
import s from './Raw.module.css';

export type RawEvent = IrEvent<number | RGB | string>;

export interface RawEventsViewProps {
  readonly events: readonly RawEvent[];
  /** Name of the track, for the list's label. */
  readonly label: string;
  onReveal(event: RawEvent, index: number): void;
  onCopy(): void;
}

function triple(t: readonly [number, number, number]): string {
  return `[${String(t[0])}, ${String(t[1])}, ${String(t[2])}]`;
}

function value(v: number | RGB | string): string {
  if (typeof v === 'number') return String(Math.round(v * 1e4) / 1e4);
  if (typeof v === 'string') return JSON.stringify(v);
  return `[${v.join(', ')}]`;
}

function num(v: number): string {
  return String(Math.round(v * 1e4) / 1e4);
}

interface Row {
  readonly e: RawEvent;
  readonly i: number;
}

export function RawEventsView(props: RawEventsViewProps) {
  const { t } = useText();
  const rows: Row[] = props.events.map((e, i) => ({ e, i }));
  return (
    <div className={s.table}>
      <div className={s.tableBar}>
        <span className={s.muted}>{t(m.eventCount, { count: props.events.length })}</span>
        <Button
          size="sm"
          icon="Copy"
          onClick={() => {
            props.onCopy();
          }}
        >
          {t(m.copy)}
        </Button>
      </div>
      <div className={s.tableHead} aria-hidden="true">
        <span>{t(m.colIndex)}</span>
        <span>{t(m.colStart)}</span>
        <span>{t(m.colEnd)}</span>
        <span>{t(m.colFrom)}</span>
        <span>{t(m.colTo)}</span>
        <span>{t(m.colEasing)}</span>
        <span>{t(m.colSlice)}</span>
        <span>{t(m.colBezier)}</span>
        <span>{t(m.colLink)}</span>
        <span>{t(m.colOrigin)}</span>
      </div>
      <VirtualList
        className={s.tableBody}
        items={rows}
        rowHeight={26}
        ariaLabel={{ key: m.tableLabel, params: { target: props.label } }}
        getKey={(r) => r.i}
        onActivate={(r) => {
          props.onReveal(r.e, r.i);
        }}
        render={(r) => {
          const e = r.e;
          const whole = e.easingLeft === 0 && e.easingRight === 1;
          return (
            <span className={s.tableRow} data-origin={e.origin} title={t(m.revealEvent, { index: r.i })}>
              <span className={s.cellIndex}>{r.i}</span>
              <span>{triple(e.startTime)}</span>
              <span>{triple(e.endTime)}</span>
              <span>{value(e.start)}</span>
              <span>{value(e.end)}</span>
              <span>{e.easingType}</span>
              <span>{whole ? t(m.sliceWhole) : `${num(e.easingLeft)} .. ${num(e.easingRight)}`}</span>
              <span>{e.bezier === 0 ? t(m.bezierNone) : e.bezierPoints.map(num).join(', ')}</span>
              <span>{e.linkgroup === 0 ? t(m.linkNone) : e.linkgroup}</span>
              <span className={s.origin}>{t(m.origin, { origin: e.origin })}</span>
            </span>
          );
        }}
      />
    </div>
  );
}
